import React, { useState } from 'react';
import {
  Drawer, Form, Input, Select, InputNumber, Checkbox,
  Button, App, Divider, Spin, Dropdown,
} from 'antd';
import {
  ThunderboltOutlined, DownloadOutlined, SaveOutlined, ReloadOutlined,
  EyeOutlined, EditOutlined,
} from '@ant-design/icons';
import ReactDOM from 'react-dom/client';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../api/client';
import MathRenderer from './MathRenderer';

// Mode key = ai_type value used by the Materials filter, so a generated file
// shows up under the right type chip automatically. "Questions" and "Exam Paper"
// each produce two files (with the matching solutions / mark scheme); the rest
// produce one.
const MODES = [
  { key: 'questions',   emoji: '📝', label: 'Questions',   desc: 'A question file plus a matching solutions file' },
  { key: 'solutions',   emoji: '💡', label: 'Solutions',   desc: 'Worked solutions for existing questions', needsSource: true, sourceLabel: 'Source questions',  sourcePlaceholder: 'Paste the questions you want solutions for…' },
  { key: 'notes',       emoji: '📋', label: 'Notes',       desc: 'Concise revision notes for a topic' },
  { key: 'exam_paper',  emoji: '📄', label: 'Exam Paper',  desc: 'An exam paper, full worked solutions, and a mark scheme' },
  { key: 'mark_scheme', emoji: '🎯', label: 'Mark Scheme', desc: 'Mark scheme for an existing exam paper', needsSource: true, sourceLabel: 'Source exam paper', sourcePlaceholder: 'Paste the exam paper…' },
];

async function callAI(type, params) {
  const res = await api.post('/api/ai/generate', { type, ...params });
  return res.data;
}

export default function AIGeneratorPanel({ open, onClose, subject, subjects, onSave }) {
  const [form]    = Form.useForm();
  const [selMode, setSelMode] = useState('questions');
  const [loading, setLoading] = useState(false);
  const [outputs, setOutputs] = useState([]);  // array of { label, content, aiType }
  const { message } = App.useApp();

  const mode = MODES.find(m => m.key === selMode);

  const handleGenerate = async (values) => {
    setLoading(true);
    setOutputs([]);
    try {
      const selectedSubject = subjects?.find(s => s.subject_id === values.subject);
      const subjectName = selectedSubject
        ? `${selectedSubject.subject_name} ${selectedSubject.level}`
        : values.subject;
      const subjectLevel = selectedSubject?.level || '';

      const baseParams = { subject: subjectName, level: subjectLevel, topic: values.topic, difficulty: values.difficulty };

      if (selMode === 'questions') {
        // Bundle: generate questions, then solutions for those questions.
        const qRes = await callAI('questions_only', { ...baseParams, count: values.count, context: values.context });
        const sRes = await callAI('solutions_only', { ...baseParams, questions: qRes.content });
        setOutputs([
          { label: 'Questions', content: qRes.content, aiType: 'questions', preview: true },
          { label: 'Solutions', content: sRes.content, aiType: 'solutions', preview: true },
        ]);
      } else if (selMode === 'solutions') {
        const res = await callAI('solutions_only', { ...baseParams, questions: values.source });
        setOutputs([{ label: 'Solutions', content: res.content, aiType: 'solutions', preview: true }]);
      } else if (selMode === 'notes') {
        const res = await callAI('notes', { ...baseParams, includeExamples: values.includeExamples });
        setOutputs([{ label: 'Notes', content: res.content, aiType: 'notes', preview: true }]);
      } else if (selMode === 'exam_paper') {
        // Bundle: generate paper, then full solutions and mark scheme in parallel.
        const pRes = await callAI('paper_only', { ...baseParams, totalMarks: values.totalMarks });
        const [sRes, mRes] = await Promise.all([
          callAI('full_solutions', { ...baseParams, questions: pRes.content }),
          callAI('mark_scheme',    { ...baseParams, questions: pRes.content }),
        ]);
        setOutputs([
          { label: 'Exam Paper',      content: pRes.content, aiType: 'exam_paper',  preview: true },
          { label: 'Full Solutions',  content: sRes.content, aiType: 'solutions',   preview: true },
          { label: 'Mark Scheme',     content: mRes.content, aiType: 'mark_scheme', preview: true },
        ]);
      } else if (selMode === 'mark_scheme') {
        const res = await callAI('mark_scheme', { ...baseParams, questions: values.source });
        setOutputs([{ label: 'Mark Scheme', content: res.content, aiType: 'mark_scheme', preview: true }]);
      }
    } catch (e) {
      message.error(e.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const updateOutput = (idx, content) => {
    setOutputs(prev => prev.map((o, i) => i === idx ? { ...o, content } : o));
  };

  const togglePreview = (idx) => {
    setOutputs(prev => prev.map((o, i) => i === idx ? { ...o, preview: !o.preview } : o));
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Render markdown+math via MathRenderer offscreen, then snapshot as a canvas
  // so math is properly typeset (rather than appearing as literal $x^2$ in exports).
  const renderToCanvas = async (content) => {
    const container = document.createElement('div');
    container.style.cssText = 'position:absolute;left:-99999px;top:0;width:780px;background:#fff;padding:40px;font-family:"Inter",-apple-system,BlinkMacSystemFont,sans-serif;color:#0a0a0a;line-height:1.7;font-size:13px;';
    document.body.appendChild(container);
    const root = ReactDOM.createRoot(container);
    root.render(<MathRenderer content={content} />);
    await new Promise(resolve => setTimeout(resolve, 300));
    const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#fff', logging: false });
    root.unmount();
    document.body.removeChild(container);
    return canvas;
  };

  const buildPDF = async (output) => {
    const canvas = await renderToCanvas(output.content);
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin    = 40;
    const imgWidth  = pageWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const usableHeight = pageHeight - margin * 2;
    const imgData   = canvas.toDataURL('image/png');
    let position = margin;
    let heightLeft = imgHeight;
    doc.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
    heightLeft -= usableHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      doc.addPage();
      doc.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= usableHeight;
    }
    return doc;
  };

  const buildWordHtml = async (output) => {
    const canvas = await renderToCanvas(output.content);
    const imgData = canvas.toDataURL('image/png');
    return `<!DOCTYPE html><html xmlns:office="urn:schemas-microsoft-com:office:office" xmlns:word="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${output.label}</title></head><body><h1>${output.label}</h1><img src="${imgData}" style="max-width:100%;" /></body></html>`;
  };

  const exportPDF = async (output) => {
    const doc = await buildPDF(output);
    doc.save(`${output.label}_${Date.now()}.pdf`);
  };

  const exportWord = async (output) => {
    const html = await buildWordHtml(output);
    const blob = new Blob([html], { type: 'application/msword' });
    downloadBlob(blob, `${output.label}_${Date.now()}.doc`);
  };

  const saveAll = async (format) => {
    if (!onSave || outputs.length === 0) return;
    try {
      for (const output of outputs) {
        const ts = Date.now();
        const uid = Math.random().toString(36).slice(2, 8);
        let blob, displayName, s3Key, mimeType;
        if (format === 'pdf') {
          const doc = await buildPDF(output);
          blob = doc.output('blob');
          displayName = `AI_${output.aiType}_${ts}.pdf`;
          s3Key = `mat_${ts}_${uid}.pdf`;          // neutral S3 key — URL won't leak ai_type
          mimeType = 'application/pdf';
        } else {
          const html = await buildWordHtml(output);
          blob = new Blob([html], { type: 'application/msword' });
          displayName = `AI_${output.aiType}_${ts}.doc`;
          s3Key = `mat_${ts}_${uid}.doc`;
          mimeType = 'application/msword';
        }
        await onSave(blob, displayName, form.getFieldValue('subject'), output.aiType, mimeType, s3Key);
      }
      message.success('Saved to materials');
    } catch (e) {
      message.error(e.message || 'Save failed');
    }
  };

  return (
    <Drawer
      title={<span style={{ fontWeight: 700, fontSize: 15 }}><ThunderboltOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Generate with AI</span>}
      open={open}
      onClose={onClose}
      width={outputs.length > 1 ? 1100 : 860}
      bodyStyle={{ padding: 0, display: 'flex', height: '100%' }}
      destroyOnClose
    >
      <div style={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>

        {/* ── Left: Form ── */}
        <div style={{ width: 300, flexShrink: 0, borderRight: '1px solid #e8e8e8', overflowY: 'auto', padding: '24px 20px' }}>
          {/* Mode selector */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 8 }}>What do you want to generate?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {MODES.map(m => (
                <div key={m.key} onClick={() => { setSelMode(m.key); setOutputs([]); }}
                  style={{
                    padding: '10px 12px', border: `1px solid ${selMode === m.key ? '#0a0a0a' : '#e8e8e8'}`,
                    borderRadius: 8, cursor: 'pointer',
                    background: selMode === m.key ? '#f5f5f5' : '#fff',
                  }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{m.emoji} {m.label}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{m.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <Form form={form} layout="vertical" requiredMark={false}
            initialValues={{ count: 3, totalMarks: 40, difficulty: 'intermediate' }}
            onFinish={handleGenerate}>

            <Form.Item name="subject" label="Subject" rules={[{ required: true, message: 'Required' }]}>
              {subjects?.length > 0
                ? <Select placeholder="Select subject"
                    options={subjects.map(s => ({ value: s.subject_id, label: `${s.subject_name} (${s.level})` }))} />
                : <Input placeholder="e.g. E-Mathematics" />
              }
            </Form.Item>

            <Form.Item name="topic" label="Topic"
              rules={[{ required: true, message: 'Enter a topic' }]}>
              <Input placeholder="e.g. Quadratic equations, Ionic bonding" />
            </Form.Item>

            {selMode !== 'notes' && (
              <Form.Item name="difficulty" label="Difficulty">
                <Select>
                  <Select.Option value="foundation">Foundation</Select.Option>
                  <Select.Option value="intermediate">Intermediate</Select.Option>
                  <Select.Option value="extension">Extension</Select.Option>
                  <Select.Option value="mixed">Mixed</Select.Option>
                </Select>
              </Form.Item>
            )}

            {selMode === 'questions' && (
              <>
                <Form.Item name="count" label="Number of questions">
                  <InputNumber min={1} max={10} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="context" label="Additional context (optional)">
                  <Input.TextArea rows={2} placeholder="Any specific constraints" />
                </Form.Item>
              </>
            )}

            {mode?.needsSource && (
              <Form.Item name="source" label={mode.sourceLabel}
                rules={[{ required: true, message: 'Paste the source content' }]}>
                <Input.TextArea rows={5} placeholder={mode.sourcePlaceholder} />
              </Form.Item>
            )}

            {selMode === 'notes' && (
              <Form.Item name="includeExamples" valuePropName="checked">
                <Checkbox>Include worked examples</Checkbox>
              </Form.Item>
            )}

            {selMode === 'exam_paper' && (
              <Form.Item name="totalMarks" label="Total marks">
                <InputNumber min={10} max={100} step={5} style={{ width: '100%' }} />
              </Form.Item>
            )}

            <Button type="primary" htmlType="submit" block loading={loading}
              icon={<ThunderboltOutlined />} style={{ background: '#0a0a0a', border: 'none', marginTop: 4 }}>
              Generate
            </Button>
          </Form>
        </div>

        {/* ── Right: Output(s) ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Action bar */}
          {outputs.length > 0 && (
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #e8e8e8', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <Button size="small" icon={<ReloadOutlined />} onClick={() => form.submit()} loading={loading}>Regenerate</Button>
              {outputs.map((o, i) => (
                <Dropdown
                  key={i}
                  menu={{
                    items: [
                      { key: 'pdf',  label: 'PDF' },
                      { key: 'word', label: 'Word' },
                    ],
                    onClick: ({ key }) => key === 'pdf' ? exportPDF(o) : exportWord(o),
                  }}
                >
                  <Button size="small" icon={<DownloadOutlined />}>
                    Export {o.label}
                  </Button>
                </Dropdown>
              ))}
              {onSave && (
                <Dropdown menu={{
                  items: [
                    { key: 'pdf',  label: 'as PDF' },
                    { key: 'word', label: 'as Word' },
                  ],
                  onClick: ({ key }) => saveAll(key),
                }}>
                  <Button size="small" type="primary" icon={<SaveOutlined />}
                    style={{ background: '#0a0a0a', border: 'none' }}>
                    Save to materials
                  </Button>
                </Dropdown>
              )}
            </div>
          )}

          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {loading && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, fontSize: 13 }}>Generating — this may take 20–60 seconds…</div>
              </div>
            )}

            {!loading && outputs.length === 0 && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                <ThunderboltOutlined style={{ fontSize: 48, marginBottom: 16, color: '#e8e8e8' }} />
                <div style={{ fontSize: 14 }}>Fill in the form and click Generate.</div>
                {mode && (
                  <div style={{ fontSize: 12, color: '#bbb', marginTop: 8 }}>{mode.desc}</div>
                )}
              </div>
            )}

            {!loading && outputs.length > 0 && outputs.map((output, i) => (
              <div key={i} style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                borderLeft: i > 0 ? '1px solid #e8e8e8' : 'none',
                overflow: 'hidden',
              }}>
                <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 12, fontWeight: 600, color: '#555', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{output.label}</span>
                  <Button
                    size="small"
                    type="text"
                    icon={output.preview ? <EditOutlined /> : <EyeOutlined />}
                    onClick={() => togglePreview(i)}
                    style={{ fontSize: 11, color: '#888' }}
                  >
                    {output.preview ? 'Edit' : 'Preview'}
                  </Button>
                </div>
                {output.preview ? (
                  <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#fff', fontSize: 13, lineHeight: 1.8 }}>
                    <MathRenderer content={output.content} />
                  </div>
                ) : (
                  <Input.TextArea
                    value={output.content}
                    onChange={e => updateOutput(i, e.target.value)}
                    style={{ flex: 1, height: '100%', resize: 'none', border: 'none', borderRadius: 0, fontFamily: 'monospace', fontSize: 12, padding: '16px' }}
                    autoSize={false}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
